import {
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  Database,
  MessageSquare,
  PlusIcon,
  Users,
  XCircle,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { ModuleSectionNav } from '../../../components/navigation/ModuleSectionNav';
import { useGetSchoolDashboardQuery } from '../../../state/domainRouterApi';
import { readDomainRouterErrorMessage } from '../../../state/domainRouterError';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { mapSchoolDashboardResponse } from '../camps/viewModels';
import { SCHOOL_CAMPS_SECTION_ITEMS } from './sectionNav';

/* ── Colour tokens ──────────────────────────────────────────────────── */
const PRIMARY = 'var(--primary, #2697a2)';
const BORDER = 'var(--border, #e5e7eb)';
const TEXT = 'var(--iron-grey, #404f4d)';
const TEXT_SECONDARY = 'var(--text-secondary, #6b7280)';
const AQUAMARINE = 'var(--aquamarine, #28E2A7)';
const CORAL = 'var(--coral, #ff5f5f)';
const STRAWBERRY = 'var(--strawberry, #fe2c3b)';

/* ── Shared type aliases for domain response shapes ─────────────────── */
type CampProgramView = {
  id?: string;
  name?: string;
  campType?: string;
  gradeMin?: string;
  gradeMax?: string;
  sessionCount?: number;
  enrolledCount?: number;
  totalCapacity?: number;
  waitlistCount?: number;
  status?: 'draft' | 'published' | 'archived';
};

type IngestHealth = {
  state?: 'healthy' | 'stale' | 'failed';
  lastRefreshAt?: string;
  sourceSystem?: string;
};

type WaitlistPressure = {
  totalWaitlisted?: number;
  sessionsAtCapacity?: number;
};

type RosterReadiness = {
  readyCount?: number;
  pendingCount?: number;
};

type EventResponseSummary = {
  pendingCommunications?: number;
  pendingPromotions?: number;
};

type DashboardSummary = {
  activeCamps?: number;
  enrolledCampers?: number;
  openSeats?: number;
  waitlistPressure?: number;
  unpaidBalanceCents?: number;
  seasonLabel?: string;
};

type OpsTelemetry = {
  latestRefreshAt?: string | number | null;
  lastSnapshotDate?: string | null;
  sourceSystem?: string;
};

type DashboardResponse = {
  summary?: DashboardSummary;
  items?: CampProgramView[];
  ingestHealth?: IngestHealth;
  waitlistPressure?: WaitlistPressure;
  rosterReadiness?: RosterReadiness;
  opsTelemetry?: OpsTelemetry;
  eventResponseSummary?: EventResponseSummary;
};

/* ── Currency formatter ─────────────────────────────────────────────── */
function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/* ── Loading skeleton matching final layout structure ────────────────── */
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* header skeleton */}
      <div className="h-8 w-48 animate-pulse rounded bg-gray-100" />
      {/* KPI row skeleton */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
      {/* table skeleton */}
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
      {/* panels skeleton */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

/* ── Error banner ───────────────────────────────────────────────────── */
function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="text-sm font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

/* ── Status dot helper ──────────────────────────────────────────────── */
function StatusDot({ status }: { status?: string }) {
  const color =
    status === 'published' ? AQUAMARINE :
    status === 'draft' ? TEXT_SECONDARY :
    status === 'archived' ? STRAWBERRY :
    TEXT_SECONDARY;

  return (
    <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: TEXT }}>
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {status === 'published' ? 'Published' : status === 'draft' ? 'Draft' : status === 'archived' ? 'Archived' : '--'}
    </span>
  );
}

/* ── Ingest state dot helper ────────────────────────────────────────── */
function IngestStateDot({ state }: { state?: string }) {
  const color =
    state === 'healthy' ? AQUAMARINE :
    state === 'stale' ? CORAL :
    state === 'failed' ? STRAWBERRY :
    TEXT_SECONDARY;
  const label =
    state === 'healthy' ? 'Healthy' :
    state === 'stale' ? 'Stale' :
    state === 'failed' ? 'Failed' :
    'Unknown';

  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: TEXT }}>
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

/* ── KPI card ───────────────────────────────────────────────────────── */
function KpiCard({ label, value, dotColor }: { label: string; value: string; dotColor: string }) {
  return (
    <div
      className="rounded-xl border bg-white p-5 shadow-sm"
      style={{ borderColor: BORDER }}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: dotColor }}
        />
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.06em]"
          style={{ color: TEXT_SECONDARY }}
        >
          {label}
        </p>
      </div>
      <p className="mt-2 text-[32px] font-semibold leading-tight" style={{ color: TEXT }}>
        {value}
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Dashboard Page
   ═══════════════════════════════════════════════════════════════════════ */

export function SchoolCampsDashboardPage() {
  const { data: rawData, isLoading, isError, error } = useGetSchoolDashboardQuery();

  if (isLoading) {
    return (
      <main
        data-testid="school-camps-dashboard"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="school-camps-dashboard"
        data-route-ready="true"
        data-extracted-from="source-faithful:school-camps-v1.0"
        className="min-h-screen bg-[var(--background-light)]"
      >
        <div className="mx-auto max-w-[1440px] space-y-6">
          <ErrorBanner
            title="Unable to load camp operations data"
            detail={readDomainRouterErrorMessage(
              error,
              'The camps backend is not yet available. Check that the domain router is deployed.'
            )}
          />
        </div>
      </main>
    );
  }

  const data = mapSchoolDashboardResponse(rawData) as DashboardResponse;
  const summary = data.summary ?? {};
  const items: CampProgramView[] = Array.isArray(data.items) ? data.items : [];
  const ingestHealth = data.ingestHealth ?? {};
  const waitlistPressureData = data.waitlistPressure ?? {};
  const rosterReadiness = data.rosterReadiness ?? {};
  const eventResponse = data.eventResponseSummary ?? {};
  const opsTelemetry = data.opsTelemetry ?? {};

  return (
    <main
      data-testid="school-camps-dashboard"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-camps-v1.0"
      className="min-h-screen bg-[var(--background-light)]"
    >
      <div className="mx-auto max-w-[1440px] space-y-6">
        {/* ── Page header ──────────────────────────────────────────── */}
        <div
          className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
          data-testid="school-camps-header"
        >
          <div>
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.08em]"
              style={{ color: PRIMARY }}
            >
              Camps
            </p>
            <h1 className="text-[24px] font-semibold" style={{ color: TEXT }}>
              Camp Operations
            </h1>
            {summary.seasonLabel ? (
              <p className="mt-0.5 text-sm" style={{ color: TEXT_SECONDARY }}>
                <CalendarDays className="mr-1 inline-block h-3.5 w-3.5" style={{ color: TEXT_SECONDARY }} />
                {summary.seasonLabel}
              </p>
            ) : null}
          </div>
          <NavLink
            to="new"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: PRIMARY }}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            New Camp
          </NavLink>
        </div>

        <ModuleSectionNav
          items={SCHOOL_CAMPS_SECTION_ITEMS}
          activeId="dashboard"
          ariaLabel="Camp operations navigation"
          mobileMenuTitle="Camp operations"
          testIds={{
            root: "school-camps-section-nav",
            itemPrefix: "school-camps-section-nav-item",
            mobileTrigger: "school-camps-section-nav-mobile-trigger",
            mobileDialog: "school-camps-section-nav-mobile-dialog",
            mobileItemPrefix: "school-camps-section-nav-mobile-item",
          }}
        />

        {/* ── KPI row ──────────────────────────────────────────────── */}
        <div
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5"
          data-testid="school-camps-kpi-row"
        >
          <KpiCard
            label="Active Camps"
            value={String(summary.activeCamps ?? 0)}
            dotColor={AQUAMARINE}
          />
          <KpiCard
            label="Enrolled Campers"
            value={String(summary.enrolledCampers ?? 0)}
            dotColor={AQUAMARINE}
          />
          <KpiCard
            label="Open Seats"
            value={String(summary.openSeats ?? 0)}
            dotColor={(summary.openSeats ?? 0) > 0 ? AQUAMARINE : CORAL}
          />
          <KpiCard
            label="Waitlist Pressure"
            value={String(summary.waitlistPressure ?? 0)}
            dotColor={(summary.waitlistPressure ?? 0) > 0 ? CORAL : AQUAMARINE}
          />
          <KpiCard
            label="Unpaid Balances"
            value={formatCurrency(summary.unpaidBalanceCents)}
            dotColor={(summary.unpaidBalanceCents ?? 0) > 0 ? CORAL : AQUAMARINE}
          />
        </div>

        {/* ── Programs table ───────────────────────────────────────── */}
        <div
          className="overflow-hidden rounded-xl border bg-white shadow-sm"
          style={{ borderColor: BORDER }}
          data-testid="school-camps-programs-table"
        >
          <div className="source-faithful-tableScroll">
          <table
            className="min-w-[920px] divide-y text-sm"
            style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
          >
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Program Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Grade Range
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Sessions
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Enrolled / Capacity
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Waitlist
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody
              className="divide-y bg-white"
              style={{ '--tw-divide-color': BORDER } as React.CSSProperties}
            >
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm font-medium" style={{ color: TEXT_SECONDARY }}>
                    No camp programs found. Create your first camp to get started.
                  </td>
                </tr>
              ) : (
                items.map((program) => (
                  <tr
                    key={program.id ?? program.name}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-medium" style={{ color: TEXT }}>
                      <NavLink
                        to={program.id ?? ''}
                        className="inline-flex items-center gap-1 hover:underline"
                        style={{ color: PRIMARY }}
                      >
                        {program.name ?? 'Untitled'}
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </NavLink>
                    </td>
                    <td className="px-6 py-4" style={{ color: TEXT }}>
                      {program.campType ?? '--'}
                    </td>
                    <td className="px-6 py-4" style={{ color: TEXT }}>
                      {program.gradeMin && program.gradeMax
                        ? `${program.gradeMin} - ${program.gradeMax}`
                        : '--'}
                    </td>
                    <td className="px-6 py-4 text-center" style={{ color: TEXT }}>
                      {program.sessionCount ?? 0}
                    </td>
                    <td className="px-6 py-4 text-center font-medium" style={{ color: TEXT }}>
                      {program.enrolledCount ?? 0} / {program.totalCapacity ?? 0}
                    </td>
                    <td className="px-6 py-4 text-center" style={{ color: TEXT }}>
                      {program.waitlistCount ?? 0}
                    </td>
                    <td className="px-6 py-4">
                      <StatusDot status={program.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* ── Ops panels row ───────────────────────────────────────── */}
        <div
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-5"
          data-testid="school-camps-ops-panels"
        >
          {/* Ingest Health */}
          <div
            className="rounded-xl border bg-white p-5 shadow-sm"
            style={{ borderColor: BORDER }}
            data-testid="school-camps-ingest-health"
          >
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: TEXT_SECONDARY }}
            >
              Ingest Health
            </p>
            <IngestStateDot state={ingestHealth.state} />
            <div className="mt-3 space-y-1.5 text-sm" style={{ color: TEXT }}>
              <p className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" style={{ color: TEXT_SECONDARY }} />
                {ingestHealth.lastRefreshAt ?? 'No data'}
              </p>
              <p className="text-xs" style={{ color: TEXT_SECONDARY }}>
                Source: {ingestHealth.sourceSystem ?? '--'}
              </p>
            </div>
          </div>

          {/* Waitlist Pressure */}
          <div
            className="rounded-xl border bg-white p-5 shadow-sm"
            style={{ borderColor: BORDER }}
            data-testid="school-camps-waitlist-pressure"
          >
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: TEXT_SECONDARY }}
            >
              Waitlist Pressure
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" style={{ color: (waitlistPressureData.totalWaitlisted ?? 0) > 0 ? CORAL : TEXT_SECONDARY }} />
                <span className="text-[24px] font-semibold" style={{ color: TEXT }}>
                  {waitlistPressureData.totalWaitlisted ?? 0}
                </span>
                <span className="text-sm" style={{ color: TEXT_SECONDARY }}>waitlisted</span>
              </div>
              <p className="text-sm" style={{ color: TEXT_SECONDARY }}>
                {waitlistPressureData.sessionsAtCapacity ?? 0} sessions at capacity
              </p>
            </div>
          </div>

          {/* Roster Readiness */}
          <div
            className="rounded-xl border bg-white p-5 shadow-sm"
            style={{ borderColor: BORDER }}
            data-testid="school-camps-roster-readiness"
          >
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: TEXT_SECONDARY }}
            >
              Roster Readiness
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" style={{ color: AQUAMARINE }} />
                <span className="text-sm font-medium" style={{ color: TEXT }}>
                  {rosterReadiness.readyCount ?? 0} ready
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" style={{ color: CORAL }} />
                <span className="text-sm font-medium" style={{ color: TEXT }}>
                  {rosterReadiness.pendingCount ?? 0} pending
                </span>
              </div>
            </div>
          </div>

          {/* Event Response */}
          <div
            className="rounded-xl border bg-white p-5 shadow-sm"
            style={{ borderColor: BORDER }}
            data-testid="school-camps-event-response"
          >
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: TEXT_SECONDARY }}
            >
              Event Response
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" style={{ color: (eventResponse.pendingCommunications ?? 0) > 0 ? CORAL : TEXT_SECONDARY }} />
                <span className="text-sm font-medium" style={{ color: TEXT }}>
                  {eventResponse.pendingCommunications ?? 0} pending communications
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4" style={{ color: (eventResponse.pendingPromotions ?? 0) > 0 ? CORAL : TEXT_SECONDARY }} />
                <span className="text-sm font-medium" style={{ color: TEXT }}>
                  {eventResponse.pendingPromotions ?? 0} pending promotions
                </span>
              </div>
            </div>
          </div>

          {/* Gap 9: Pipeline Telemetry */}
          <div
            className="rounded-xl border bg-white p-5 shadow-sm"
            style={{ borderColor: BORDER }}
            data-testid="school-camps-pipeline-telemetry"
          >
            <p
              className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em]"
              style={{ color: TEXT_SECONDARY }}
            >
              Pipeline Telemetry
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" style={{ color: TEXT_SECONDARY }} />
                <span className="text-sm" style={{ color: TEXT }}>
                  {opsTelemetry.latestRefreshAt
                    ? typeof opsTelemetry.latestRefreshAt === 'number'
                      ? new Date(opsTelemetry.latestRefreshAt * 1000).toLocaleString()
                      : String(opsTelemetry.latestRefreshAt)
                    : 'No data'}
                </span>
              </div>
              <p className="text-sm" style={{ color: TEXT }}>
                <span style={{ color: TEXT_SECONDARY }}>Snapshot:</span>{' '}
                {opsTelemetry.lastSnapshotDate ?? '--'}
              </p>
              <div className="flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" style={{ color: TEXT_SECONDARY }} />
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: `${PRIMARY}15`, color: PRIMARY }}
                >
                  {opsTelemetry.sourceSystem ?? 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
